import React, { useState, useEffect, lazy, Suspense, useCallback } from 'react';
import { motion } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import copy from 'copy-to-clipboard';
import apiService, { WithdrawalLimits, WithdrawalRequest } from '../../services/api';
import { BalanceResponse } from '../../types/wallet';
import AppLayout from '../shared/AppLayout';
import { useDesktopView } from '../../hooks/useDesktopView';
import { useWithdrawalPolling } from '../../hooks/useWithdrawalPolling';
import SwapTab from './SwapTab';

// Lazy load desktop component
const DesktopWalletPage = lazy(() => import('./desktop/DesktopWalletPage'));

// Loading fallback for desktop
const DesktopLoadingFallback: React.FC = () => (
  <div className="min-h-screen bg-black text-white flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <motion.div
        className="w-10 h-10 border-3 border-gray-600 border-t-white rounded-full"
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
      />
      <p className="text-gray-500">Loading...</p>
    </div>
  </div>
);

type TabType = 'deposit' | 'withdraw' | 'swap';

// Mobile Wallet Page Component
const MobileWalletPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('deposit');

  // Deposit state
  const [balance, setBalance] = useState<BalanceResponse | null>(null);
  const [walletAddress, setWalletAddress] = useState<string>('');
  const [copied, setCopied] = useState(false);

  // Withdrawal state
  const [limits, setLimits] = useState<WithdrawalLimits | null>(null);
  const [withdrawals, setWithdrawals] = useState<WithdrawalRequest[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [token, setToken] = useState<'SOL' | 'USDC'>('SOL');
  const [amount, setAmount] = useState('');
  const [destinationAddress, setDestinationAddress] = useState('');
  const [password, setPassword] = useState('');

  // Shared state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [pendingWithdrawalId, setPendingWithdrawalId] = useState<number | null>(null);

  useEffect(() => {
    // Get wallet address from user info
    const userInfo = localStorage.getItem('user');
    if (userInfo) {
      const user = JSON.parse(userInfo);
      setWalletAddress(user.wallet_address || '');
    }

    fetchAllData();

    // Poll balance every 10 seconds
    const interval = setInterval(fetchBalance, 10000);
    return () => clearInterval(interval);
  }, []);

  // Handle withdrawal completion
  const handleWithdrawalComplete = useCallback(async (withdrawal: WithdrawalRequest) => {
    setSuccess(`Withdrawal of ${Number(withdrawal.amount).toFixed(withdrawal.token === 'SOL' ? 4 : 2)} ${withdrawal.token} completed successfully!`);
    setPendingWithdrawalId(null);
    // Refresh limits and balance after successful withdrawal
    const limitsData = await apiService.withdrawals.getLimits();
    setLimits(limitsData);
    await fetchBalance();
  }, []);

  // Handle withdrawal failure
  const handleWithdrawalFailed = useCallback((withdrawal: WithdrawalRequest) => {
    setError(`Withdrawal failed: ${withdrawal.error_message || 'Unknown error'}`);
    setSuccess(null);
    setPendingWithdrawalId(null);
  }, []);

  // Poll for status updates when there's a pending/processing withdrawal
  useWithdrawalPolling({
    withdrawals,
    onWithdrawalsUpdate: setWithdrawals,
    onComplete: handleWithdrawalComplete,
    onFailed: handleWithdrawalFailed,
  });

  const fetchAllData = async () => {
    setLoading(true);
    await Promise.all([fetchBalance(), fetchWithdrawalData()]);
    setLoading(false);
  };

  const fetchBalance = async () => {
    try {
      const data = await apiService.wallet.getBalance();
      setBalance(data);
    } catch (err: any) {
      console.error('Failed to fetch balance:', err);
    }
  };

  const fetchWithdrawalData = async () => {
    try {
      const [limitsData, withdrawalsData] = await Promise.all([
        apiService.withdrawals.getLimits(),
        apiService.withdrawals.getList(),
      ]);
      setLimits(limitsData);
      setWithdrawals(withdrawalsData);
    } catch (err: any) {
      console.error('Failed to fetch withdrawal data:', err);
    }
  };

  const handleCopyAddress = () => {
    copy(walletAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleWithdrawSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (!destinationAddress || destinationAddress.length < 32) {
      setError('Please enter a valid Solana address');
      return;
    }

    if (!password) {
      setError('Please enter your password to authorize the withdrawal');
      return;
    }

    if (limits) {
      const minAmount = token === 'SOL' ? limits.min_withdrawal_sol : limits.min_withdrawal_usdc;
      const available = token === 'SOL' ? limits.available_sol : limits.available_usdc;

      if (amountNum < minAmount) {
        setError(`Minimum withdrawal is ${minAmount} ${token}`);
        return;
      }

      if (amountNum > available) {
        setError(`Insufficient balance. Available: ${available.toFixed(token === 'SOL' ? 4 : 2)} ${token}`);
        return;
      }
    }

    try {
      setSubmitting(true);
      const withdrawal = await apiService.withdrawals.create({
        amount: amountNum,
        token,
        destination_address: destinationAddress,
        password,
      });

      // Show pending message - the actual success will be shown when polling detects completion
      setSuccess('Withdrawal submitted! Processing transaction...');
      setPendingWithdrawalId(withdrawal.id);
      setAmount('');
      setDestinationAddress('');
      setPassword('');
      await fetchWithdrawalData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit withdrawal');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelWithdrawal = async (id: number) => {
    try {
      await apiService.withdrawals.cancel(id);
      // Refresh both withdrawal data and balance to keep UI in sync
      await Promise.all([fetchWithdrawalData(), fetchBalance()]);
      setSuccess('Withdrawal cancelled');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to cancel withdrawal');
    }
  };

  const handleSetMax = () => {
    if (limits) {
      const maxAmount = token === 'SOL' ? limits.available_sol : limits.available_usdc;
      setAmount(maxAmount.toFixed(token === 'SOL' ? 4 : 2));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-400';
      case 'pending':
      case 'processing': return 'text-yellow-400';
      case 'failed':
      case 'cancelled': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  if (loading) {
    return (
      <AppLayout title="Wallet">
        <div className="flex items-center justify-center py-20">
          <div className="text-gray-400">Loading...</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Wallet">
      {/* Tab Selector */}
      <div className="flex gap-2 mb-6">
        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={() => { setActiveTab('deposit'); setError(null); setSuccess(null); }}
          className={`flex-1 py-3 rounded-xl font-semibold transition-colors ${
            activeTab === 'deposit'
              ? 'bg-white text-black'
              : 'bg-gray-900 text-gray-400 hover:bg-gray-800'
          }`}
        >
          Deposit
        </motion.button>
        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={() => { setActiveTab('withdraw'); setError(null); setSuccess(null); }}
          className={`flex-1 py-3 rounded-xl font-semibold transition-colors ${
            activeTab === 'withdraw'
              ? 'bg-white text-black'
              : 'bg-gray-900 text-gray-400 hover:bg-gray-800'
          }`}
        >
          Withdraw
        </motion.button>
        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={() => { setActiveTab('swap'); setError(null); setSuccess(null); }}
          className={`flex-1 py-3 rounded-xl font-semibold transition-colors ${
            activeTab === 'swap'
              ? 'bg-white text-black'
              : 'bg-gray-900 text-gray-400 hover:bg-gray-800'
          }`}
        >
          Swap
        </motion.button>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4"
        >
          <p className="text-red-400 text-sm">{error}</p>
        </motion.div>
      )}
      {success && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`${pendingWithdrawalId ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-green-500/10 border-green-500/30'} border rounded-xl p-4 mb-4`}
        >
          <div className="flex items-center gap-2">
            {pendingWithdrawalId && (
              <svg className="animate-spin h-4 w-4 text-yellow-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            <p className={`${pendingWithdrawalId ? 'text-yellow-400' : 'text-green-400'} text-sm`}>{success}</p>
          </div>
        </motion.div>
      )}

      {/* DEPOSIT TAB */}
      {activeTab === 'deposit' && (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          key="deposit"
        >
          {/* SOL First Requirement Alert */}
          {balance && balance.requires_sol_first && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-purple-500/10 border border-purple-500/30 rounded-2xl p-4 mb-6"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-purple-400 font-bold text-sm">SOL</span>
                </div>
                <div>
                  <h3 className="text-purple-300 font-semibold mb-1">
                    Important: Deposit SOL First
                  </h3>
                  <p className="text-purple-200/80 text-sm">
                    {balance.sol_first_message || 'Your first deposit must be SOL. SOL is required to pay transaction fees (gas) on Solana.'}
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* QR Code */}
          <div className="flex flex-col items-center mb-6">
            <div className="bg-white p-4 rounded-2xl mb-4">
              <QRCodeSVG
                value={walletAddress}
                size={180}
                level="H"
                includeMargin={true}
              />
            </div>

            {/* Wallet Address */}
            <div className="w-full bg-gray-900 rounded-2xl p-4">
              <label className="text-sm text-gray-500 block mb-2">
                Your Wallet Address
              </label>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-gray-300 text-xs break-all">
                  {walletAddress}
                </code>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={handleCopyAddress}
                  className={`px-4 py-2 rounded-xl font-semibold transition-colors ${
                    copied
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-gray-800 hover:bg-gray-700 text-white'
                  }`}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </motion.button>
              </div>
            </div>
          </div>

          {/* Balance Display */}
          {balance && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-900 rounded-2xl p-6 mb-6"
            >
              <h2 className="text-sm font-medium text-gray-500 mb-4">Current Balance</h2>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-3xl font-bold text-white">
                    {balance.balance_sol.toFixed(4)} SOL
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-white">
                    {balance.balance_usdc.toFixed(2)} USDC
                  </p>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-800">
                {balance.has_deposited ? (
                  <div className="flex items-center gap-2 text-green-400">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="font-semibold">Wallet Funded</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-yellow-400">
                    <svg className="w-5 h-5 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                    </svg>
                    <span>Waiting for deposit...</span>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Instructions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gray-900 rounded-2xl p-6"
          >
            <h3 className="text-sm font-medium text-gray-500 mb-3">How to Deposit</h3>
            <ol className="space-y-2 text-sm text-gray-400">
              <li className={balance?.requires_sol_first ? 'text-purple-300 font-medium' : ''}>
                1. {balance?.requires_sol_first ? 'First, deposit SOL (required for gas fees)' : 'Scan the QR code or copy the wallet address'}
              </li>
              <li>2. {balance?.requires_sol_first ? 'Then add SOL or USDC for trading capital' : 'Send SOL or USDC from any wallet or exchange'}</li>
              <li>3. Wait for confirmation (10-30 seconds)</li>
              <li>4. Balance updates automatically</li>
            </ol>
          </motion.div>
        </motion.div>
      )}

      {/* WITHDRAW TAB */}
      {activeTab === 'withdraw' && (
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          key="withdraw"
        >
          {/* Available Balance */}
          {limits && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-900 rounded-2xl p-6 mb-6"
            >
              <h3 className="text-sm font-medium text-gray-500 mb-4">Available Balance</h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm text-gray-500">SOL</p>
                  <p className="text-2xl font-bold text-white">{limits.available_sol.toFixed(4)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">USDC</p>
                  <p className="text-2xl font-bold text-white">${limits.available_usdc.toFixed(2)}</p>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-800">
                <p className="text-xs text-gray-500">
                  No daily withdrawal limits - withdraw your full available balance
                </p>
              </div>
            </motion.div>
          )}

          {/* Withdrawal Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gray-900 rounded-2xl p-6 mb-6"
          >
            <form onSubmit={handleWithdrawSubmit} className="space-y-5">
              {/* Token Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-3">
                  Select Token
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <motion.button
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    onClick={() => setToken('SOL')}
                    className={`py-3 rounded-xl font-semibold transition-colors ${
                      token === 'SOL'
                        ? 'bg-white text-black'
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    SOL
                  </motion.button>
                  <motion.button
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    onClick={() => setToken('USDC')}
                    className={`py-3 rounded-xl font-semibold transition-colors ${
                      token === 'USDC'
                        ? 'bg-white text-black'
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    USDC
                  </motion.button>
                </div>
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Amount
                </label>
                <div className="flex gap-3">
                  <input
                    type="number"
                    step={token === 'SOL' ? '0.0001' : '0.01'}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder={`Enter ${token} amount`}
                    className="flex-1 px-4 py-3 bg-black border border-gray-700
                             text-white rounded-xl focus:outline-none focus:border-gray-500"
                  />
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    type="button"
                    onClick={handleSetMax}
                    className="px-4 py-3 bg-gray-800 hover:bg-gray-700
                             border border-gray-700 text-white font-semibold rounded-xl"
                  >
                    MAX
                  </motion.button>
                </div>
              </div>

              {/* Destination Address */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Destination Address
                </label>
                <input
                  type="text"
                  value={destinationAddress}
                  onChange={(e) => setDestinationAddress(e.target.value)}
                  placeholder="Enter Solana wallet address"
                  className="w-full px-4 py-3 bg-black border border-gray-700
                           text-white rounded-xl focus:outline-none focus:border-gray-500
                           font-mono text-sm"
                />
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your account password"
                  className="w-full px-4 py-3 bg-black border border-gray-700
                           text-white rounded-xl focus:outline-none focus:border-gray-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Required to authorize the withdrawal
                </p>
              </div>

              {/* Submit */}
              <motion.button
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={submitting || !amount || !destinationAddress || !password}
                className="w-full py-4 bg-white hover:bg-gray-100
                         text-black font-bold rounded-xl transition-colors
                         disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Executing withdrawal...' : `Withdraw ${token}`}
              </motion.button>
            </form>
          </motion.div>

          {/* Recent Withdrawals */}
          {withdrawals.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-gray-900 rounded-2xl p-6"
            >
              <h3 className="text-sm font-medium text-gray-500 mb-4">Recent Withdrawals</h3>
              <div className="space-y-4">
                {withdrawals.slice(0, 5).map((w) => (
                  <div
                    key={w.id}
                    className="flex items-center justify-between py-3 border-b border-gray-800 last:border-0"
                  >
                    <div>
                      <p className="text-white font-mono">
                        {Number(w.amount).toFixed(w.token === 'SOL' ? 4 : 2)} {w.token}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(w.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-semibold ${getStatusColor(w.status)}`}>
                        {w.status.charAt(0).toUpperCase() + w.status.slice(1)}
                      </p>
                      {w.status === 'pending' && (
                        <motion.button
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleCancelWithdrawal(w.id)}
                          className="mt-1 text-xs text-red-400 hover:text-red-300"
                        >
                          Cancel
                        </motion.button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </motion.div>
      )}

      {/* SWAP TAB */}
      {activeTab === 'swap' && (
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          key="swap"
        >
          <SwapTab
            onError={setError}
            onSuccess={setSuccess}
            onBalanceChange={fetchBalance}
            isDesktop={false}
          />
        </motion.div>
      )}
    </AppLayout>
  );
};

// Main WalletPage component with responsive switching
const WalletPage: React.FC = () => {
  const { isDesktop } = useDesktopView();

  if (isDesktop) {
    return (
      <Suspense fallback={<DesktopLoadingFallback />}>
        <DesktopWalletPage />
      </Suspense>
    );
  }

  return <MobileWalletPage />;
};

export default WalletPage;
